import React, { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SubscriptionPlans from '@/components/SubscriptionPlans';
import EnterpriseLicensing from '@/components/EnterpriseLicensing';
import { AppHeader } from '@/components/AppHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export default function Subscription() {
  const [user, setUser] = useState<User | null>(null);
  useNavigationHistory(); // Track navigation
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <div className="container mx-auto py-12 px-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="plans">Individual Plans</TabsTrigger>
            <TabsTrigger value="enterprise">Enterprise Licensing</TabsTrigger>
          </TabsList>

          <TabsContent value="plans">
            {/* Legal Acceptance */}
            <div className="max-w-2xl mx-auto mb-8 space-y-4 bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Before you subscribe</h2>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="terms" 
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                  />
                  <Label 
                    htmlFor="terms" 
                    className="text-sm leading-relaxed cursor-pointer"
                  >
                    I have read and agree to the{' '}
                    <button
                      onClick={() => window.open('/terms', '_blank')}
                      className="text-primary underline hover:text-primary/80"
                    >
                      Terms & Conditions
                    </button>
                  </Label>
                </div>
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="privacy" 
                    checked={privacyAccepted}
                    onCheckedChange={(checked) => setPrivacyAccepted(checked as boolean)}
                  />
                  <Label 
                    htmlFor="privacy" 
                    className="text-sm leading-relaxed cursor-pointer"
                  >
                    I have read and understand the{' '}
                    <button
                      onClick={() => window.open('/privacy', '_blank')}
                      className="text-primary underline hover:text-primary/80"
                    >
                      Privacy Policy
                    </button>
                  </Label>
                </div>
              </div>
              {(!termsAccepted || !privacyAccepted) && (
                <p className="text-sm text-muted-foreground mt-4">
                  Please accept both the Terms & Conditions and Privacy Policy to proceed with subscription.
                </p>
              )}
            </div>

            <SubscriptionPlans disabled={!termsAccepted || !privacyAccepted} />
          </TabsContent>

          <TabsContent value="enterprise">
            <EnterpriseLicensing />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}